import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { selectDomainID } from "../../AsianExchangeSrc/utils/helper/commonSelectors";
import {
  login,
  getCurrentRecord,
  createLoginRecord,
  Demo,
} from "../../AsianExchangeSrc/api"; // Replace with actual paths
import { loginSuccess } from "../../AsianExchangeSrc/redux/slices/userSlice";
import { setAccountType } from "../../AsianExchangeSrc/redux/slices/accountSlice";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const domainId = useSelector(selectDomainID());

  const [formData, setFormData] = useState({
    userName: "",
    password: "",
    domainId: domainId,
    x_panel_type: "user",
  });

  const [loading, setLoading] = useState({ real: false });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!formData.userName || !formData.password) {
      toast.error("Please fill all fields");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setLoading((prev) => ({ ...prev, real: true }));

    try {
      const response = await login(formData);
      if (response.success) {
        const { token, userdata } = response.data;

        localStorage.setItem("userId", userdata?._id);

        dispatch(
          loginSuccess({
            userData: userdata,
            loggedInType: "real",
            token: token,
          })
        );

        dispatch(
          setAccountType({
            userData: userdata,
            type: "real",
          })
        );

        toast.success(response.data.message || "Login successful");

        navigate("/");

        const res = await getCurrentRecord();
        await createLoginRecord(res.data);
      } else {
        toast.error(response.data.message || "Invalid credentials");
      }
    } catch (error) {
      console.error("Login API error:", error);
      toast.error("Failed to login. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, real: false }));
    }
  };

  const handleDemoAccount = async () => {
    setLoading((prev) => ({ ...prev, demo: true }));

    try {
      const res = await Demo();

      if (!res || !res.data || !res.data.token) {
        throw new Error("Invalid response from Demo API");
      }

      const { token, userdata } = res.data;

      dispatch(setAccountType({ type: "demo" }));
      dispatch(
        loginSuccess({
          userData: userdata,
          loggedInType: "demo",
          token: token,
        })
      );

      toast.success("Demo account created successfully");

      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error creating demo account:", error);
      toast.error("Failed to create demo account. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, demo: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0088cc] to-[#2c3e50] flex flex-col items-center justify-center text-white font-sans">
      <h1 className="text-6xl font-bold mb-10">ALL</h1>

      <form
        onSubmit={handleSubmit}
        className="bg-white text-black p-6 rounded-md shadow-md w-80 text-center"
      >
        <h2 className="text-xl font-semibold mb-6 flex justify-center items-center gap-2">
          LOGIN{" "}
          <span role="img" aria-label="pin">
            📌
          </span>
        </h2>

        <div className="mb-4 relative">
          <input
            type="text"
            name="userName"
            value={formData.userName}
            onChange={handleChange}
            placeholder="Username"
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded focus:outline-none"
          />
          <span className="absolute top-2.5 right-3 text-gray-500">
            <i className="fas fa-user"></i>
          </span>
        </div>

        <div className="mb-4 relative">
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Password"
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded focus:outline-none"
          />
          <span className="absolute top-2.5 right-3 text-gray-500">
            <i className="fas fa-key"></i>
          </span>
        </div>

        <button
          type="submit"
          disabled={loading.real}
          className="w-full bg-[#0088cc] hover:bg-blue-700 text-white font-semibold py-2 rounded flex justify-center items-center gap-2 mb-2"
        >
          {loading.real ? "Logging in..." : "Login"}{" "}
          <i className="fas fa-sign-in-alt"></i>
        </button>

        <button
          type="button"
          className="w-full bg-[#0088cc] hover:bg-blue-700 text-white font-semibold py-2 rounded flex justify-center items-center gap-2"
          onClick={handleDemoAccount}
          disabled={loading.demo}
        >
          {loading.demo ? (
            <>
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              Logging in...
            </>
          ) : (
            <>
              Login with demo ID <i className="fas fa-sign-in-alt"></i>
            </>
          )}
        </button>

        <p className="text-xs mt-4 text-gray-500">
          This site is protected by reCAPTCHA and the Google{" "}
          <a href="#" className="text-blue-500 hover:underline">
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href="#" className="text-blue-500 hover:underline">
            Terms of Service
          </a>{" "}
          apply.
        </p>

        <div className="mt-4 text-sm">
          <span>New User?</span>{" "}
          <a href="#" className="text-blue-600 hover:underline font-medium">
            Create an account
          </a>
        </div>
      </form>

      <div className="absolute bottom-0 w-full bg-[#0088cc] text-white text-sm flex justify-between px-4 py-2">
        <div className="flex gap-4">
          <a href="#" className="hover:underline">
            Terms and Conditions
          </a>
          <a href="#" className="hover:underline">
            Responsible Gaming
          </a>
        </div>
        <div className="font-bold">24X7 Support</div>
      </div>
    </div>
  );
};

export default Login;
